
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CoachStats {
  totalCoaches: number;
  coachesWithTeams: number;
  coachesWithoutTeams: number;
  multiClubCoaches: number;
  teamCoverage: number;
  coaches: CoachDetail[];
}

interface CoachDetail {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  teams: Array<{
    id: string;
    name: string;
    club_name: string;
  }>;
  lastLogin?: string;
}

export const useCoachStats = (selectedClubId?: string) => {
  const [stats, setStats] = useState<CoachStats>({
    totalCoaches: 0,
    coachesWithTeams: 0,
    coachesWithoutTeams: 0,
    multiClubCoaches: 0,
    teamCoverage: 0,
    coaches: []
  });
  const [loading, setLoading] = useState(true);

  const fetchCoachStats = useCallback(async () => {
    try {
      setLoading(true);

      // Since we no longer have 'coach' role, we'll get all club coaches
      // This represents people who are assigned as coaches to clubs
      const { data: clubCoaches, error: clubCoachesError } = await supabase
        .from('club_coaches')
        .select(`
          *,
          profiles!inner(
            id,
            first_name,
            last_name,
            email
          ),
          clubs!inner(
            id,
            name
          )
        `);

      if (clubCoachesError) throw clubCoachesError;

      // Transform club coaches data to match the expected format
      const coachesMap = new Map();
      
      clubCoaches?.forEach(clubCoach => {
        const coachId = clubCoach.profiles.id;
        if (!coachesMap.has(coachId)) {
          coachesMap.set(coachId, {
            id: coachId,
            first_name: clubCoach.profiles.first_name,
            last_name: clubCoach.profiles.last_name,
            email: clubCoach.profiles.email,
            teams: [],
            clubAssociations: []
          });
        }
        
        const coach = coachesMap.get(coachId);
        coach.clubAssociations.push({
          club_id: clubCoach.club_id,
          club_name: clubCoach.clubs.name
        });
      });

      // Now get teams for each coach through team_coaches relationship
      for (const [coachId, coach] of coachesMap) {
        const { data: teamCoaches, error: teamCoachesError } = await supabase
          .from('team_coaches')
          .select(`
            teams!inner(
              id,
              name,
              club_id,
              clubs:clubs!club_id(name)
            )
          `)
          .eq('coach_id', coachId)
          .eq('is_active', true);

        if (!teamCoachesError && teamCoaches) {
          coach.teams = teamCoaches.map(tc => ({
            id: tc.teams.id,
            name: tc.teams.name,
            club_name: tc.teams.clubs?.name || 'Unknown Club'
          }));
        }
      }

      let filteredCoaches = Array.from(coachesMap.values());

      // Filter coaches by club if a specific club is selected
      if (selectedClubId && selectedClubId !== 'all') {
        filteredCoaches = filteredCoaches.filter(coach => 
          coach.clubAssociations?.some((assoc: { club_id: string; club_name: string }) => assoc.club_id === selectedClubId) ||
          coach.teams?.some((team: { id: string; name: string; club_name: string; club_id?: string }) => team.club_id === selectedClubId)
        );
      }

      // Calculate statistics
      const totalCoaches = filteredCoaches.length;
      const coachesWithTeams = filteredCoaches.filter(coach => 
        coach.teams && coach.teams.length > 0
      ).length;
      const coachesWithoutTeams = totalCoaches - coachesWithTeams;

      // Multi-club coaches (coaches with teams in different clubs)
      const multiClubCoaches = filteredCoaches.filter(coach => {
        if (!coach.teams || coach.teams.length <= 1) return false;
        const clubIds = new Set(coach.teams.map((team: { id: string; name: string; club_name: string; club_id?: string }) => team.club_id).filter(Boolean));
        return clubIds.size > 1;
      }).length;

      // Team coverage calculation - now we need to check which teams have active coaches
      let teamCoverageQuery = supabase
        .from('teams')
        .select(`
          id, 
          name, 
          clubs:clubs!club_id(name),
          team_coaches!inner(coach_id, is_active)
        `);

      if (selectedClubId && selectedClubId !== 'all') {
        teamCoverageQuery = teamCoverageQuery.eq('club_id', selectedClubId);
      }

      const { data: teams, error: teamsError } = await teamCoverageQuery.eq('team_coaches.is_active', true);

      if (teamsError) throw teamsError;

      // Get all teams for the calculation
      let allTeamsQuery = supabase
        .from('teams')
        .select('id, name, clubs:clubs!club_id(name)');

      if (selectedClubId && selectedClubId !== 'all') {
        allTeamsQuery = allTeamsQuery.eq('club_id', selectedClubId);
      }

      const { data: allTeams, error: allTeamsError } = await allTeamsQuery;

      if (allTeamsError) throw allTeamsError;

      const totalTeams = allTeams?.length || 0;
      const teamsWithCoaches = teams?.length || 0;
      const teamCoverage = totalTeams > 0 ? Math.round((teamsWithCoaches / totalTeams) * 100) : 0;

      // Format coach details
      const coachDetails: CoachDetail[] = filteredCoaches.map(coach => ({
        id: coach.id,
        first_name: coach.first_name,
        last_name: coach.last_name,
        email: coach.email,
        teams: coach.teams || []
      }));

      setStats({
        totalCoaches,
        coachesWithTeams,
        coachesWithoutTeams,
        multiClubCoaches,
        teamCoverage,
        coaches: coachDetails
      });
    } catch (error) {
      console.error('Error fetching coach stats:', error);
      toast.error('Failed to load coach statistics');
    } finally {
      setLoading(false);
    }
  }, [selectedClubId]);

  useEffect(() => {
    fetchCoachStats();
  }, [fetchCoachStats]);

  return {
    stats,
    loading,
    refetch: fetchCoachStats
  };
};
